import clsx from 'clsx'
import { GetStaticPropsContext } from 'next'
import { useMemo, useState } from 'react'
import { SWRConfig } from 'swr'
import { FahrlehrerCh } from '@/src/@types/blinkadmin'
import { PageParams, PreviewData } from '@/src/@types/infrastructur'
import { BaseDataType, FlchPages } from '@/src/@types/statamic'
import { PageProps } from '@/src/@types/util'
import FlchHeader from '@/src/blocks/FlchHeader/Component'
import CookieBanner from '@/src/components/elements/CookieBanner'
import FaqJsonLd from '@/src/components/elements/FaqJsonLd'
import {
  buildBreadcrumbs,
  TEACHER_LOCATION_BREADCRUMB,
} from '@/src/components/flch/breadcrumbs'
import BreadcrumbListJsonLd from '@/src/components/flch/components/BreadcrumbListJsonLd'
import BaseLayout from '@/src/components/flch/navigation/BaseLayout'
import { getFlchFallback } from '@/src/components/flch/pages/fallback'
import Filter from '@/src/components/flch/teacherLocation/Filter'
import List, {
  getLocationLayout,
} from '@/src/components/flch/teacherLocation/List'
import Container from '@/src/components/layout/Container'
import MetaTags from '@/src/components/MetaTags'
import FlchBaseData from '@/src/graphql/FlchBaseData.graphql'
import FlchPageQuery from '@/src/graphql/FlchPageQuery.graphql'
import { filterNavs } from '@/src/lib/filterNavs'
import {
  getBlockDescription,
  getBlockImage,
  getBlockTitle,
  loadBlockProps,
  renderBlock,
} from '@/src/lib/PageBuilder'
import { ensureTrailingSlash } from '@/src/lib/str'
import { batchRequests, statamicConfig } from '@/src/StatamicGraphQLClient'

const BASE_URL = process.env.BASE_URL

type Props = PageProps<typeof getStaticProps>

export async function getStaticProps(
  context: GetStaticPropsContext<PageParams, PreviewData>,
  values: FahrlehrerCh.LocationPage
) {
  const urlParts = [...(context.params?.uri || [])]
  const currentUri = `/${urlParts.join('/')}`

  const [{ entry }, { navs, settings, flchFallbacks }] = await batchRequests<
    [{ entry: FlchPages['entry'] | null }, BaseDataType]
  >(
    [
      {
        document: FlchPageQuery,
        variables: {
          uri: currentUri,
          locale: `${process.env.NEXT_PUBLIC_STATAMIC_SITE_HANDLE}_de`,
          collections: statamicConfig.collections,
        },
      },
      {
        document: FlchBaseData,
        variables: {
          locale: `${process.env.NEXT_PUBLIC_STATAMIC_SITE_HANDLE}_de`,
          settingsHandle: statamicConfig.settingsHandle,
        },
      },
    ],
    context.previewData?.statamicToken
  )

  const filteredNavs = filterNavs(navs)

  const fallback = entry
    ? null
    : getFlchFallback(
        flchFallbacks,
        'teacher',
        values.swissPlaceName,
        values.cantonName
      )

  const page = {
    header_text: entry?.header_text ?? fallback?.header_text ?? null,
    seo_title: entry?.seo_title ?? fallback?.seo_title ?? null,
    seo_summary: entry?.seo_summary ?? fallback?.seo_summary ?? null,
    social_title: entry?.social_title ?? null,
    social_text: entry?.social_text ?? null,
    social_image: entry?.social_image ?? null,
  }

  const blocks = await loadBlockProps(
    entry?.flch_page_builder ?? fallback?.flch_page_builder,
    context
  )

  if (!page.social_title) {
    page.social_title = page.seo_title
  }
  if (!page.social_text) {
    page.social_text = page.seo_summary
  }

  if (
    !page.social_title ||
    !page.seo_summary ||
    !page.social_text ||
    !page.social_image
  ) {
    for (const block of blocks) {
      const title = getBlockTitle(block)
      if (title && !page.social_title) {
        page.social_title = title
      }

      const text = getBlockDescription(block)
      if (text && !page.social_text) {
        page.social_text = text
      }
      if (text && !page.seo_summary) {
        page.seo_summary = text
      }

      const image = getBlockImage(block)
      if (image && !page.social_image) {
        page.social_image = image
      }

      if (
        page.social_title &&
        page.seo_summary &&
        page.social_text &&
        page.social_image
      ) {
        break
      }
    }
  }

  const uri = context.params?.uri ? context.params?.uri?.join('/') : ''

  return {
    props: {
      blocks,
      navs: filteredNavs,
      settings,
      seoTitle: page.seo_title || null,
      seoSummary: page.seo_summary || null,
      socialTitle: page.social_title || null,
      socialText: page.social_text || null,
      socialImage: page.social_image || null,
      date: entry?.date ?? null,
      fullUrl: uri ? BASE_URL + '/' + ensureTrailingSlash(uri) : BASE_URL,
      noindex: entry?.noindex ?? false,
      headerText: page.header_text || null,
      values,
    },
  }
}

const TeacherLocation = ({
  blocks,
  navs,
  settings,
  seoTitle,
  seoSummary,
  socialTitle,
  socialText,
  socialImage,
  date,
  fullUrl,
  values,
  noindex,
  headerText,
}: Props) => {
  const { localTeachers, mode } = useMemo(
    () => getLocationLayout(values.teachers, values.locationId),
    [values.teachers, values.locationId]
  )

  const bookable = localTeachers.length > 0

  const locationOptions = useMemo(() => {
    const byId = new Map<
      number,
      { id: number; name: string; distanceKm: number }
    >()
    for (const teacher of values.teachers) {
      if (!byId.has(teacher.location.id)) {
        byId.set(teacher.location.id, {
          id: teacher.location.id,
          name: teacher.location.name,
          distanceKm: teacher.location.distanceKm,
        })
      }
    }
    return [...byId.values()]
      .sort((a, b) => a.distanceKm - b.distanceKm)
      .map(({ id, name }) => ({ id, name }))
  }, [values.teachers])

  const filterPool = mode === 'single' ? localTeachers : values.teachers

  const [filteredTeachers, setFilteredTeachers] =
    useState<FahrlehrerCh.LocationPageTeacher[]>(filterPool)

  const title =
    mode === 'single'
      ? `${filterPool.length} Fahrlehrer:innen in ${values.swissPlaceName}`
      : `${filterPool.length} Fahrlehrer:innen in ${values.swissPlaceName} und Umgebung`

  const headerTitle = `Fahrschule ${values.swissPlaceName}`
  const breadcrumbs = buildBreadcrumbs(headerTitle, TEACHER_LOCATION_BREADCRUMB)

  return (
    <>
      <MetaTags
        seoTitle={seoTitle}
        seoSummary={seoSummary}
        title={socialTitle}
        description={socialText}
        fullUrl={fullUrl}
        date={date}
        image={socialImage}
        noindex={noindex}
      />
      <CookieBanner />
      <FaqJsonLd blocks={blocks} />
      <BreadcrumbListJsonLd breadcrumbs={breadcrumbs} fullUrl={fullUrl} />
      <BaseLayout navs={navs} settings={settings}>
        <FlchHeader
          type="flch_header"
          header_type={{ value: 'light' }}
          pageInfo={{
            settings,
            socialImage,
            fullUrl,
            blueprint: 'pages',
            newSpacing: true,
          }}
          images={[]}
          title={headerTitle}
          heading="Fahrlehrer:innen vergleichen und finden"
          breadcrumbs={breadcrumbs}
          hero_content={[
            {
              type: 'text',
              text: headerText ?? '',
            },
          ]}
          search={false}
        />
        <Container fullPageOnMobile className="w-full">
          <div className="my-12 lg:mt-24 lg:flex">
            <h2 className="mx-5 text-4xl font-black md:mx-0 lg:hidden">
              {title}
            </h2>
            <div className="mt-4 ml-5 md:ml-0 lg:mt-0 lg:w-75 lg:shrink-0">
              <Filter
                teachers={filterPool}
                setFilteredTeachers={setFilteredTeachers}
              />
            </div>
            <div className="mx-5 mt-4 md:mx-0 lg:mt-0 lg:ml-16 lg:grow xl:ml-34">
              <h2 className="hidden text-4xl font-black lg:block">{title}</h2>
              <List filteredTeachers={filteredTeachers} {...values} />
            </div>
          </div>
        </Container>
        <div
          className={clsx({
            'pb-20 md:pb-32':
              blocks[blocks.length - 1]?.block !== 'flch_contact_local' &&
              blocks[blocks.length - 1]?.block !== 'flch_book_trial_lesson',
          })}
        >
          {blocks.map((block, index) => (
            <div key={index} className="relative">
              <span
                id={`${block.block}-${
                  blocks.slice(0, index).filter((b) => b.block === block.block)
                    .length + 1
                }`}
                className="absolute -top-20"
              ></span>
              <SWRConfig value={{ fallback: block.fallback ?? {} }}>
                {renderBlock(block, index, blocks, {
                  settings,
                  socialImage,
                  fullUrl,
                  blueprint: 'pages',
                  newSpacing: true,
                  flchSettings: {
                    locationName: values.swissPlaceName,
                    locationId:
                      (mode === 'single' || mode === 'mixed') &&
                      values.locationId !== null
                        ? values.locationId
                        : undefined,
                    bookable,
                    locations: locationOptions,
                    crosslinks: {
                      currentOffer: 'lessons',
                      nearbyLocations: values.nearbyLocations,
                      otherOffers: values.otherOffers,
                    },
                  },
                })}
              </SWRConfig>
            </div>
          ))}
        </div>
      </BaseLayout>
    </>
  )
}

export default TeacherLocation
